import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext.tsx';
import { useRealtime } from './RealtimeContext.tsx';
import { AppNotification } from '../../shared/types.ts';

import { playIncomingMessageSound } from '../services/soundEffects.ts';

// Re-export professional office chime as playLoudAlertSound for backwards compatibility
export const playLoudAlertSound = playIncomingMessageSound;

export interface ToastAlert {
  title: string;
  message: string;
  type?: string;
  channelId?: string;
  taskId?: string;
  projectId?: string;
  linkUrl?: string;
}

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  toast: ToastAlert | null;
  clearToast: () => void;
  browserPermission: NotificationPermission;
  requestNotificationPermission: () => Promise<NotificationPermission>;
  playSound: () => void;
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  fetchNotifications: async () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  toast: null,
  clearToast: () => {},
  browserPermission: 'default',
  requestNotificationPermission: async () => 'default',
  playSound: () => {},
});

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const { subscribe } = useRealtime();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [toast, setToast] = useState<ToastAlert | null>(null);
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission>(() => {
    return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default';
  });

  const requestNotificationPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'denied';
    }
    try {
      const res = await Notification.requestPermission();
      setBrowserPermission(res);
      if (res === 'granted') {
        playLoudAlertSound();
        new Notification('IHB Pulse Notifications Enabled', {
          body: 'You will receive loud alerts for mentions, assigned tasks, and completions.',
        });
      }
      return res;
    } catch {
      return 'denied';
    }
  }, []);

  const triggerDesktopNotification = useCallback(
    (title: string, body: string, meta?: Partial<ToastAlert>) => {
      if (
        typeof window !== 'undefined' &&
        'Notification' in window &&
        Notification.permission === 'granted'
      ) {
        // While the person is looking at Pulse the in-app toast and number are enough; the native
        // alert is for when the tab is in the background or the window is not focused
        if (document.visibilityState === 'visible' && document.hasFocus()) return;
        try {
          const n = new Notification(title, {
            body,
            icon: '/favicon.png',
            tag: meta?.channelId ? `ihb-pulse-chat-${meta.channelId}` : `ihb-pulse-${Date.now()}`,
          });
          n.onclick = () => {
            window.focus();
            if (meta) {
              window.dispatchEvent(
                new CustomEvent('navigate-notification', { detail: meta })
              );
            }
            n.close();
          };
        } catch (e) {
          console.error('Failed to dispatch desktop notification:', e);
        }
      }
    },
    []
  );

  // The realtime handler below outlives renders, so it calls the latest fetch through a ref
  const fetchNotificationsRef = useRef<() => Promise<void>>(async () => {});

  const fetchNotifications = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/notifications', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  };

  fetchNotificationsRef.current = fetchNotifications;

  useEffect(() => {
    if (user && token) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user?.id, token]);

  // Auto-dismiss toast after 8 seconds if not clicked
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => {
      setToast(null);
    }, 8000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Subscribe to real-time notification events
  useEffect(() => {
    const unsubscribe = subscribe((event) => {
      if (
        event.type === 'notification' ||
        event.type === 'mention' ||
        event.type === 'task_assigned' ||
        event.type === 'task_completed'
      ) {
        const notif = event.data;
        if (!notif) return;

        // Play loud sound alert immediately
        playLoudAlertSound();

        const toastMeta: ToastAlert = {
          title: notif.title || 'Notification',
          message: notif.message || '',
          type: notif.type || event.type,
          channelId: notif.channelId || notif.channel_id,
          taskId: notif.taskId || notif.task_id,
          projectId: notif.projectId || notif.project_id,
          linkUrl: notif.linkUrl || notif.link_url,
        };

        // Trigger native desktop notification
        triggerDesktopNotification(
          toastMeta.title,
          toastMeta.message || 'You have a new update.',
          toastMeta
        );

        setNotifications((prev) => [notif, ...prev]);
        setUnreadCount((c) => c + 1);
        setToast(toastMeta);
      } else if (event.type === 'channel_read') {
        // This person read a chat: its message alerts were marked read on the server, so reload
        // the list and the bell number follows
        if ((event.data as any)?.userId === user?.id) {
          fetchNotificationsRef.current();
        }
      } else if (event.type === 'chat_message') {
        const msg = event.data;
        if (msg && msg.senderId !== user?.id) {
          // Play subtle professional office chime
          playIncomingMessageSound();

          const channelId = msg.channelId || msg.channel_id;
          const isGroup =
            (msg.channelType && msg.channelType !== 'direct') ||
            (typeof channelId === 'string' &&
              (channelId.startsWith('chan-grp-') || channelId.startsWith('chan-proj-')));
          const toastTitle = isGroup
            ? `💬 ${msg.senderName || 'Member'} in #${msg.channelName || 'Group'}`
            : `💬 ${msg.senderName || 'New Message'}`;

          const toastMeta: ToastAlert = {
            title: toastTitle,
            message: msg.content?.slice(0, 80) || 'Sent an attachment',
            type: 'chat',
            channelId,
            linkUrl: channelId ? `/chat?channelId=${channelId}` : '/chat',
          };

          triggerDesktopNotification(
            toastMeta.title,
            msg.content?.slice(0, 100) || 'Sent an attachment',
            toastMeta
          );

          setToast(toastMeta);
        }
      }
    });

    return unsubscribe;
  }, [subscribe, triggerDesktopNotification, user?.id]);

  const markAsRead = async (id: string) => {
    if (!token) return;
    try {
      await fetch(`/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {}
  };

  const markAllAsRead = async () => {
    if (!token) return;
    try {
      await fetch(`/api/notifications/read-all`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (e) {}
  };

  const clearToast = () => {
    setToast(null);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        toast,
        clearToast,
        browserPermission,
        requestNotificationPermission,
        playSound: playLoudAlertSound,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
